/* ==========================================================================
   amazon-bridge.js
   Amazon 브릿지 랜딩 페이지 동작 (Dawn 15.x 호환, 외부 라이브러리 없음)

   구성:
     1. dataLayer / GA4 이벤트 헬퍼
     2. <amz-clamp>      리뷰 본문 접기·펼치기 (넘칠 때만 버튼 노출)
     3. <amz-carousel>   scroll-snap 캐러셀 + 화살표 + 도트 + 자동재생
     4. <amz-clip>       UGC 영상 리뷰 (뷰포트 진입 시 무음 재생, 탭하면 소리)
     5. 하단 고정 CTA 노출 제어
     6. Amazon 아웃바운드 클릭 트래킹

   같은 파일이 여러 섹션에서 중복 로드돼도 1회만 실행됩니다.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__amzBridgeLoaded) return;
  window.__amzBridgeLoaded = true;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     1. 트래킹 헬퍼
     ------------------------------------------------------------------------ */
  function track(eventName, params) {
    var payload = Object.assign({ event: eventName }, params || {});

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params || {});
    }
  }

  window.amzTrack = track;

  /* Meta Pixel · TikTok Pixel 전송
     이벤트명은 일부러 'AmazonBridgeClick' 으로, 상시 판매 제품 페이지에서 쓰는
     'BuyOnAmazonClick'(Meta) / 'ClickButton'(TikTok) 과 다르게 짓습니다.
     이 파일은 amazon-bridge 템플릿에서만 로드되므로 상시 판매 페이지에는
     영향이 없고, 광고 관리자에서 브릿지 캠페인 전용 맞춤 전환을 이 이벤트
     하나만 보고 따로 만들 수 있습니다 — 상시 판매 제품의 기존 전환 리포트와
     섞이지 않습니다.

     예전에 theme.liquid 에 있던 사이트 전역 스크립트는 preventDefault 후
     setTimeout 안에서 window.open 을 호출해 팝업 차단에 막히는 문제가
     있었습니다. 여기서는 기본 이동을 막지 않고 픽셀만 발사하므로 링크가
     정상적으로 열립니다. */
  var AMZ_BRIDGE_EVENT = 'AmazonBridgeClick';

  function trackPixels(d) {
    var price = parseFloat(d.productPrice || '0') || 0;
    var pid = d.productId || '';
    var pname = d.productTitle || document.title;
    var currency = d.currency || 'USD';

    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', AMZ_BRIDGE_EVENT, {
        content_ids: [pid],
        content_name: pname,
        content_type: 'product',
        value: price,
        currency: currency,
        quantity: 1,
        contents: [{ id: pid, quantity: 1, item_price: price }]
      });
    }

    if (window.ttq && typeof window.ttq.track === 'function') {
      window.ttq.track(AMZ_BRIDGE_EVENT, {
        content_id: pid,
        content_name: pname,
        content_type: 'product',
        value: price,
        currency: currency,
        quantity: 1,
        contents: [{ content_id: pid, content_name: pname, quantity: 1, price: price }]
      });
    }
  }

  /* ------------------------------------------------------------------------
     2. <amz-clamp> — 리뷰 본문 접기/펼치기
     ------------------------------------------------------------------------ */
  var AmzClamp = (function () {
    function AmzClampElement() {
      return Reflect.construct(HTMLElement, [], AmzClampElement);
    }

    AmzClampElement.prototype = Object.create(HTMLElement.prototype);
    AmzClampElement.prototype.constructor = AmzClampElement;
    Object.setPrototypeOf(AmzClampElement, HTMLElement);

    AmzClampElement.prototype.connectedCallback = function () {
      if (this.__init) return;
      this.__init = true;

      this.textEl = this.querySelector('.amz-clamp__text');
      this.toggleEl = this.querySelector('.amz-clamp__toggle');
      if (!this.textEl || !this.toggleEl) return;

      this.labelMore = this.toggleEl.dataset.labelMore || 'See more';
      this.labelLess = this.toggleEl.dataset.labelLess || 'See less';

      this.onToggle = this.onToggle.bind(this);
      this.measure = this.measure.bind(this);

      this.toggleEl.addEventListener('click', this.onToggle);

      // 레이아웃 확정 후에 측정해야 넘침 여부가 정확합니다.
      window.requestAnimationFrame(this.measure);

      // 웹폰트 로딩 후 줄 수가 바뀌므로 한 번 더 측정합니다.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.measure);
      }
      window.addEventListener('resize', this.debouncedMeasure());
    };

    AmzClampElement.prototype.debouncedMeasure = function () {
      var self = this;
      if (this.__debounced) return this.__debounced;
      var timer = null;
      this.__debounced = function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (!self.classList.contains('is-expanded')) self.measure();
        }, 180);
      };
      return this.__debounced;
    };

    AmzClampElement.prototype.measure = function () {
      var overflowing = this.textEl.scrollHeight - this.textEl.clientHeight > 4;
      this.classList.toggle('is-clampable', overflowing);
      if (!overflowing) {
        this.classList.remove('is-expanded');
        this.toggleEl.setAttribute('aria-expanded', 'false');
      }
    };

    AmzClampElement.prototype.onToggle = function () {
      var expanded = this.classList.toggle('is-expanded');
      this.toggleEl.textContent = expanded ? this.labelLess : this.labelMore;
      this.toggleEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      if (expanded) {
        track('review_expand', {
          review_author: this.dataset.author || '',
          section_id: this.closest('[data-amz-section]')
            ? this.closest('[data-amz-section]').dataset.amzSection
            : ''
        });
      }
    };

    return AmzClampElement;
  })();

  if (!window.customElements.get('amz-clamp')) {
    window.customElements.define('amz-clamp', AmzClamp);
  }

  /* ------------------------------------------------------------------------
     3. <amz-carousel> — scroll-snap 캐러셀
     ------------------------------------------------------------------------ */
  var AmzCarousel = (function () {
    function AmzCarouselElement() {
      return Reflect.construct(HTMLElement, [], AmzCarouselElement);
    }

    AmzCarouselElement.prototype = Object.create(HTMLElement.prototype);
    AmzCarouselElement.prototype.constructor = AmzCarouselElement;
    Object.setPrototypeOf(AmzCarouselElement, HTMLElement);

    AmzCarouselElement.prototype.connectedCallback = function () {
      if (this.__init) return;
      this.__init = true;

      this.track = this.querySelector('.amz-track');
      if (!this.track) return;

      this.slides = Array.prototype.slice.call(this.track.children);
      this.prevBtn = this.querySelector('.amz-arrow--prev');
      this.nextBtn = this.querySelector('.amz-arrow--next');
      this.dotsWrap = this.querySelector('.amz-dots');
      this.autoplay = this.dataset.autoplay === 'true' && !REDUCED_MOTION;
      this.interval = parseInt(this.dataset.autoplaySpeed || '5', 10) * 1000;
      this.timer = null;

      this.onScroll = this.throttle(this.syncState.bind(this), 120);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', this.step.bind(this, -1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', this.step.bind(this, 1));
      }

      // connectedCallback 시점에는 레이아웃이 아직 확정되지 않아 카드 폭이 0으로
      // 측정됩니다. 다음 프레임에서 계산해야 도트 개수와 화살표 상태가 맞습니다.
      this.refresh = this.refresh.bind(this);
      window.requestAnimationFrame(this.refresh);

      // 브레이크포인트 전환(모바일 1.1장 → 데스크탑 3장)이나 폰트 로딩으로
      // 카드 폭이 바뀌면 도트를 다시 만들어야 합니다.
      if ('ResizeObserver' in window) {
        var rafId = null;
        var self = this;
        this.resizeObserver = new ResizeObserver(function () {
          if (rafId) window.cancelAnimationFrame(rafId);
          rafId = window.requestAnimationFrame(self.refresh);
        });
        this.resizeObserver.observe(this.track);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.refresh);
      }

      if (this.autoplay) {
        this.start();
        this.addEventListener('mouseenter', this.stop.bind(this));
        this.addEventListener('mouseleave', this.start.bind(this));
        this.addEventListener('focusin', this.stop.bind(this));
        this.track.addEventListener('touchstart', this.stop.bind(this), { passive: true });
      }
    };

    AmzCarouselElement.prototype.throttle = function (fn, wait) {
      var last = 0;
      var pending = null;
      return function () {
        var now = Date.now();
        clearTimeout(pending);
        if (now - last >= wait) {
          last = now;
          fn();
        } else {
          pending = setTimeout(function () {
            last = Date.now();
            fn();
          }, wait - (now - last));
        }
      };
    };

    AmzCarouselElement.prototype.slideStep = function () {
      if (this.slides.length < 2) return this.track.clientWidth;
      var a = this.slides[0].getBoundingClientRect();
      var b = this.slides[1].getBoundingClientRect();
      return Math.max(b.left - a.left, 1);
    };

    AmzCarouselElement.prototype.perView = function () {
      return Math.max(1, Math.round(this.track.clientWidth / this.slideStep()));
    };

    AmzCarouselElement.prototype.currentIndex = function () {
      return Math.round(this.track.scrollLeft / this.slideStep());
    };

    AmzCarouselElement.prototype.step = function (dir) {
      this.stop();
      var target = this.track.scrollLeft + dir * this.slideStep() * Math.max(1, this.perView() - 0);
      this.track.scrollTo({ left: target, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    };

    AmzCarouselElement.prototype.goTo = function (index) {
      this.stop();
      this.track.scrollTo({
        left: index * this.slideStep(),
        behavior: REDUCED_MOTION ? 'auto' : 'smooth'
      });
    };

    AmzCarouselElement.prototype.refresh = function () {
      if (!this.isConnected) return;
      this.buildDots();
      this.syncState();
    };

    AmzCarouselElement.prototype.buildDots = function () {
      if (!this.dotsWrap) return;

      var pages = Math.max(1, this.slides.length - this.perView() + 1);
      this.dotsWrap.innerHTML = '';

      // 카드가 노출 개수 이하라 스크롤할 것이 없으면 도트를 감춥니다.
      if (pages < 2) {
        this.dotsWrap.hidden = true;
        return;
      }
      this.dotsWrap.hidden = false;

      for (var i = 0; i < pages; i++) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Go to item ' + (i + 1));
        btn.dataset.index = String(i);
        btn.addEventListener('click', this.goTo.bind(this, i));
        li.appendChild(btn);
        this.dotsWrap.appendChild(li);
      }
    };

    AmzCarouselElement.prototype.syncState = function () {
      var maxScroll = this.track.scrollWidth - this.track.clientWidth;
      var left = this.track.scrollLeft;

      if (this.prevBtn) this.prevBtn.disabled = left <= 2;
      if (this.nextBtn) this.nextBtn.disabled = left >= maxScroll - 2;

      if (this.dotsWrap && !this.dotsWrap.hidden) {
        var idx = this.currentIndex();
        var dots = this.dotsWrap.querySelectorAll('button');
        for (var i = 0; i < dots.length; i++) {
          dots[i].setAttribute('aria-current', i === idx ? 'true' : 'false');
        }
      }
    };

    AmzCarouselElement.prototype.start = function () {
      if (!this.autoplay) return;
      this.stop();
      var self = this;
      this.timer = setInterval(function () {
        var maxScroll = self.track.scrollWidth - self.track.clientWidth;
        if (self.track.scrollLeft >= maxScroll - 2) {
          self.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          self.track.scrollTo({
            left: self.track.scrollLeft + self.slideStep(),
            behavior: 'smooth'
          });
        }
      }, this.interval);
    };

    AmzCarouselElement.prototype.stop = function () {
      clearInterval(this.timer);
      this.timer = null;
    };

    AmzCarouselElement.prototype.disconnectedCallback = function () {
      this.stop();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    };

    return AmzCarouselElement;
  })();

  if (!window.customElements.get('amz-carousel')) {
    window.customElements.define('amz-carousel', AmzCarousel);
  }

  /* ------------------------------------------------------------------------
     4. <amz-clip> — UGC 영상 리뷰
     ------------------------------------------------------------------------ */
  var activeUnmuted = null;

  var AmzClip = (function () {
    function AmzClipElement() {
      return Reflect.construct(HTMLElement, [], AmzClipElement);
    }

    AmzClipElement.prototype = Object.create(HTMLElement.prototype);
    AmzClipElement.prototype.constructor = AmzClipElement;
    Object.setPrototypeOf(AmzClipElement, HTMLElement);

    AmzClipElement.prototype.connectedCallback = function () {
      if (this.__init) return;
      this.__init = true;

      this.card = this.querySelector('.amz-clip');
      this.video = this.querySelector('video');
      this.poster = this.querySelector('.amz-clip__poster');
      this.soundBtn = this.querySelector('.amz-clip__sound');
      this.embedSrc = this.dataset.embed || '';
      this.autoplayInView = this.dataset.autoplayInView === 'true' && !REDUCED_MOTION;
      this.clipName = this.dataset.clipName || '';
      this.played = false;

      if (this.video) this.setupNativeVideo();
      else if (this.embedSrc && this.poster) this.setupEmbed();
    };

    /* --- Shopify 호스팅 영상 (mp4) --- */
    AmzClipElement.prototype.setupNativeVideo = function () {
      var self = this;

      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('webkit-playsinline', '');
      this.video.controls = false;

      if (this.poster) {
        this.poster.addEventListener('click', function () {
          self.poster.style.display = 'none';
          self.playSafely();
          self.unmute();
        });
      }

      if (this.soundBtn) {
        this.soundBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          if (self.video.muted) self.unmute();
          else self.mute();
        });
      }

      this.video.addEventListener('click', function () {
        if (self.video.paused) self.playSafely();
        else self.video.pause();
      });

      if (this.autoplayInView && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
                if (self.poster) self.poster.style.display = 'none';
                self.playSafely();
              } else {
                self.video.pause();
                if (self === activeUnmuted) self.mute();
              }
            });
          },
          { threshold: [0, 0.55, 1] }
        );
        this.observer.observe(this);
      }
    };

    AmzClipElement.prototype.playSafely = function () {
      var self = this;
      var p = this.video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          /* 브라우저가 자동재생을 막은 경우 포스터를 다시 노출합니다. */
          if (self.poster) self.poster.style.display = 'block';
        });
      }
      if (!this.played) {
        this.played = true;
        track('ugc_video_play', { clip_name: this.clipName });
      }
    };

    AmzClipElement.prototype.unmute = function () {
      if (activeUnmuted && activeUnmuted !== this) activeUnmuted.mute();
      this.video.muted = false;
      if (this.card) this.card.classList.add('is-unmuted');
      activeUnmuted = this;
      track('ugc_video_unmute', { clip_name: this.clipName });
    };

    AmzClipElement.prototype.mute = function () {
      this.video.muted = true;
      if (this.card) this.card.classList.remove('is-unmuted');
      if (activeUnmuted === this) activeUnmuted = null;
    };

    /* --- YouTube / Vimeo 임베드 --- */
    AmzClipElement.prototype.setupEmbed = function () {
      var self = this;
      this.poster.addEventListener('click', function () {
        var iframe = document.createElement('iframe');
        iframe.setAttribute('src', self.embedSrc);
        iframe.setAttribute('title', self.clipName || 'Customer video');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute(
          'allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        );
        iframe.setAttribute('allowfullscreen', '');
        self.poster.parentNode.appendChild(iframe);
        self.poster.remove();
        if (self.soundBtn) self.soundBtn.remove();
        track('ugc_video_play', { clip_name: self.clipName, player: 'embed' });
      });
    };

    AmzClipElement.prototype.disconnectedCallback = function () {
      if (this.observer) this.observer.disconnect();
      if (activeUnmuted === this) activeUnmuted = null;
    };

    return AmzClipElement;
  })();

  if (!window.customElements.get('amz-clip')) {
    window.customElements.define('amz-clip', AmzClip);
  }

  /* ------------------------------------------------------------------------
     4-B. <amz-gallery> — 상품 히어로 썸네일 전환
     ------------------------------------------------------------------------ */
  var AmzGallery = (function () {
    function AmzGalleryElement() {
      return Reflect.construct(HTMLElement, [], AmzGalleryElement);
    }

    AmzGalleryElement.prototype = Object.create(HTMLElement.prototype);
    AmzGalleryElement.prototype.constructor = AmzGalleryElement;
    Object.setPrototypeOf(AmzGalleryElement, HTMLElement);

    AmzGalleryElement.prototype.connectedCallback = function () {
      if (this.__init) return;
      this.__init = true;

      this.main = this.querySelector('.hero__img');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-amz-thumb]'));
      if (!this.main || this.thumbs.length === 0) return;

      var self = this;
      this.thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var src = btn.dataset.amzThumb;
          if (!src) return;

          // srcset 이 남아 있으면 브라우저가 이전 이미지를 계속 쓰므로 함께 비웁니다.
          self.main.removeAttribute('srcset');
          self.main.removeAttribute('sizes');
          self.main.src = src;

          self.thumbs.forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });
        });
      });
    };

    return AmzGalleryElement;
  })();

  if (!window.customElements.get('amz-gallery')) {
    window.customElements.define('amz-gallery', AmzGallery);
  }

  /* ------------------------------------------------------------------------
     5. 하단 고정 CTA 노출 제어
        - 지정 픽셀만큼 스크롤하면 슬라이드 업
        - 페이지 안의 다른 Amazon CTA가 화면에 보이면 숨김 (중복 방지)
     ------------------------------------------------------------------------ */
  function initStickyCta() {
    var sticky = document.querySelector('[data-amz-sticky]');
    if (!sticky) return;

    // 고정 CTA가 페이지 마지막 섹션의 콘텐츠(예: 캐러셀 도트)를 가리지
    // 않도록, 고정 바 높이만큼 본문 하단에 항상 여백을 확보해둡니다.
    // (섹션 순서를 바꿔도 항상 안전하도록 고정 바 표시 여부와 무관하게 적용)
    function reserveStickySpace() {
      var h = sticky.offsetHeight;
      document.body.style.paddingBottom = h ? h + 16 + 'px' : '';
    }
    reserveStickySpace();
    window.addEventListener('resize', reserveStickySpace);
    window.addEventListener('load', reserveStickySpace);

    // "항상 표시" 켜짐 — 스크롤 추적·중복 방지 로직을 전부 건너뛰고 상시 노출.
    // 서버 렌더링 시점에 이미 is-visible 클래스가 붙어 있으므로 여기서는
    // 뒤에 나오는 스크롤 리스너/IntersectionObserver 설정만 막으면 됩니다.
    if (sticky.dataset.always === 'true') {
      sticky.classList.add('is-visible');
      return;
    }

    var threshold = parseInt(sticky.dataset.showAfter || '400', 10);
    var inlineCtas = Array.prototype.slice.call(
      document.querySelectorAll('.amz-cta-wrap [data-amz-cta]')
    );
    var inlineVisible = false;

    if (inlineCtas.length && 'IntersectionObserver' in window) {
      var visibleSet = new Set();
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) visibleSet.add(entry.target);
            else visibleSet.delete(entry.target);
          });
          inlineVisible = visibleSet.size > 0;
          update();
        },
        { threshold: 0.35 }
      );
      inlineCtas.forEach(function (el) {
        io.observe(el);
      });
    }

    function update() {
      var show = window.scrollY > threshold && !inlineVisible;
      sticky.classList.toggle('is-visible', show);
    }

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          update();
          ticking = false;
        });
      },
      { passive: true }
    );

    update();
  }

  /* ------------------------------------------------------------------------
     6. Amazon 아웃바운드 클릭 트래킹
        Amazon Attribution 링크로 이동하기 직전에 GA4 / GTM 이벤트를 발사합니다.
     ------------------------------------------------------------------------ */
  function initOutboundTracking() {
    document.addEventListener(
      'click',
      function (e) {
        var link = e.target.closest ? e.target.closest('[data-amz-cta]') : null;
        if (!link) return;

        track('amazon_outbound_click', {
          cta_location: link.dataset.amzCta || 'unknown',
          amazon_url: link.href,
          asin: link.dataset.asin || '',
          product_handle: link.dataset.productHandle || '',
          product_id: link.dataset.productId || '',
          product_title: link.dataset.productTitle || '',
          value: parseFloat(link.dataset.productPrice || '0') || 0,
          currency: link.dataset.currency || 'USD'
        });

        trackPixels(link.dataset);
      },
      true
    );
  }

  /* ------------------------------------------------------------------------
     부트스트랩
     ------------------------------------------------------------------------ */
  function boot() {
    initStickyCta();
    initOutboundTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // 테마 에디터에서 섹션을 다시 렌더링할 때 고정 CTA 상태를 재계산합니다.
  document.addEventListener('shopify:section:load', function () {
    initStickyCta();
  });
})();
